/**
 * dApp identity for WalletConnect requests (ENTX-A-4).
 *
 * A dApp's `metadata.name` / `metadata.url` are self-declared — "Uniswap" can
 * be anyone. WalletConnect's Verify API attests the origin that actually
 * opened the pairing (`verifyContext.verified`):
 *
 *  - VALID:   the attested origin equals the dApp's declared url;
 *  - INVALID: the attested origin differs from the declared url — `origin` is
 *             the REAL origin (the page that asked), the name is a lie;
 *  - UNKNOWN: Verify could not attest anything; `origin` is just the declared
 *             url, so it is shown as unverified;
 *  - isScam:  the attested origin is on the Verify scam list → refuse.
 *
 * Only this resolved origin is ever shown as "who is asking" or stored on a
 * proposal / signature request; the self-declared name is secondary text.
 */

import type { WcVerifyContext } from '@/types/walletconnect';

export type DappVerification = 'verified' | 'mismatch' | 'unverified' | 'scam';

export interface DappIdentity {
  /** Origin to show as "who is asking" (attested when verified/mismatch/scam). */
  origin: string | null;
  verification: DappVerification;
  /** Self-declared name, for secondary display only. */
  claimedName: string;
  /** Origin of the self-declared url, when parseable. */
  claimedOrigin: string | null;
}

/** Parse anything URL-ish to its origin; null for non-http(s) or garbage. */
export function toHttpOrigin(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/** Resolve who is really asking, from the Verify context + declared metadata. */
export function resolveDappIdentity(
  verifyContext: WcVerifyContext | undefined | null,
  metadata: { name?: unknown; url?: unknown } | undefined | null
): DappIdentity {
  const claimedName = typeof metadata?.name === 'string' ? metadata.name.slice(0, 80) : '';
  const claimedOrigin = toHttpOrigin(metadata?.url);
  const verified = verifyContext?.verified;
  const attested = toHttpOrigin(verified?.origin);

  if (verified?.isScam) {
    return { origin: attested ?? claimedOrigin, verification: 'scam', claimedName, claimedOrigin };
  }
  if (verified?.validation === 'VALID' && attested) {
    return { origin: attested, verification: 'verified', claimedName, claimedOrigin };
  }
  if (verified?.validation === 'INVALID' && attested) {
    return { origin: attested, verification: 'mismatch', claimedName, claimedOrigin };
  }
  // UNKNOWN (or no Verify data at all): nothing is attested — show the claim,
  // explicitly labelled as unverified.
  return { origin: claimedOrigin, verification: 'unverified', claimedName, claimedOrigin };
}

/**
 * Plain-text label recorded on proposals and signature requests (memo /
 * dappOrigin), which co-signers and devices see. Origin-based, never the
 * self-declared name.
 */
export function dappRecordLabel(
  identity: DappIdentity,
  suffixes: { unverified: string; mismatch: string; unknownOrigin: string }
): string {
  const origin = identity.origin ?? suffixes.unknownOrigin;
  switch (identity.verification) {
    case 'verified':
      return origin;
    case 'mismatch':
    case 'scam':
      return `${origin} ${suffixes.mismatch}`;
    case 'unverified':
      return `${origin} ${suffixes.unverified}`;
  }
}

/**
 * Approval gate for a request: never for a Verify-flagged scam; for a domain
 * mismatch only after the user explicitly acknowledged it.
 */
export function dappIdentityAllowsApproval(identity: DappIdentity, acknowledged: boolean): boolean {
  if (identity.verification === 'scam') return false;
  if (identity.verification === 'mismatch') return acknowledged;
  return true;
}
